import { Box, Checkbox, IconButton, SvgIcon, Typography } from '@mui/material';
import type { Label } from '@vy/types';
import type { FC, MouseEvent } from 'react';
import { ColorDot } from '../../../design/ColorDot';

const EditIcon: FC = () => (
  <SvgIcon sx={{ fontSize: 16 }}>
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M10.1478 3.09762C10.6057 2.72413 11.2815 2.7503 11.7084 3.17704L12.8256 4.29488L12.9056 4.38342C13.2541 4.81086 13.2542 5.42806 12.9056 5.85543L12.8256 5.94397L5.9408 12.8274C5.72188 13.0469 5.42503 13.1686 5.11658 13.1686H3.33337C3.05723 13.1686 2.83337 12.9447 2.83337 12.6686V10.8854C2.83342 10.5765 2.95579 10.2795 3.17582 10.0605L10.0593 3.17704L10.1478 3.09762ZM3.83337 12.1686H5.11658C5.16091 12.1686 5.20281 12.1515 5.23311 12.1211L5.23376 12.1198L12.1185 5.23694L12.14 5.2109C12.1764 5.15574 12.1763 5.08317 12.14 5.02795L12.1185 5.00191L11.0013 3.88473C10.9447 3.82809 10.8568 3.82078 10.7924 3.86324L10.7663 3.88473L3.8822 10.7682L3.88155 10.7695C3.85113 10.7998 3.83342 10.8411 3.83337 10.8854V12.1686Z" fill="#323232"/>
      <path d="M8.81319 4.42C8.99626 4.23693 9.28607 4.22529 9.48246 4.38549L9.52022 4.42L11.5801 6.4799L11.6146 6.51766C11.7749 6.71405 11.7632 7.00382 11.5801 7.18693C11.397 7.37003 11.1072 7.38173 10.9108 7.22143L10.8731 7.18693L8.81319 5.12703L8.77868 5.08927C8.61848 4.89288 8.63012 4.60307 8.81319 4.42Z" fill="#323232"/>
    </svg>
  </SvgIcon>
);

const DeleteIcon: FC = () => (
  <SvgIcon sx={{ fontSize: 16 }}>
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M12.6667 4.16406C12.9428 4.16406 13.1667 4.38792 13.1667 4.66406C13.1667 4.9402 12.9428 5.16406 12.6667 5.16406H3.33337C3.05723 5.16406 2.83337 4.9402 2.83337 4.66406C2.83337 4.38792 3.05723 4.16406 3.33337 4.16406H12.6667Z" fill="#EE2049"/>
      <path d="M3.5 11.9974V4.66406C3.5 4.38792 3.72386 4.16406 4 4.16406C4.27614 4.16406 4.5 4.38792 4.5 4.66406V11.9974C4.5 12.4579 4.87281 12.8307 5.33333 12.8307H10.6667C11.1272 12.8307 11.5 12.4579 11.5 11.9974V4.66406C11.5 4.38792 11.7239 4.16406 12 4.16406C12.2761 4.16406 12.5 4.38792 12.5 4.66406V11.9974C12.5 13.0102 11.6795 13.8307 10.6667 13.8307H5.33333C4.32052 13.8307 3.5 13.0102 3.5 11.9974Z" fill="#EE2049"/>
      <path d="M10 2C10.2761 2 10.5 2.22386 10.5 2.5C10.5 2.77614 10.2761 3 10 3H6C5.72386 3 5.5 2.77614 5.5 2.5C5.5 2.22386 5.72386 2 6 2H10Z" fill="#EE2049"/>
    </svg>
  </SvgIcon>
);

type LabelItemProps = {
  label: Label;
  selected: boolean;
  onToggle: () => void;
  onEdit?: (mouseEvent: MouseEvent<HTMLButtonElement>) => void;
  onDelete?: () => void;
  disabled?: boolean;
};

export const LabelItem: FC<LabelItemProps> = ({ label, selected, onToggle, onEdit, onDelete, disabled }) => {
  const handleEditClick = (mouseEvent: MouseEvent<HTMLButtonElement>) => {
    mouseEvent.stopPropagation();
    onEdit?.(mouseEvent);
  };

  const handleDeleteClick = (mouseEvent: MouseEvent) => {
    mouseEvent.stopPropagation();
    onDelete?.();
  };

  return (
    <Box
      onClick={disabled ? undefined : onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: 2,
        py: 1.5,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        backgroundColor: selected ? 'background.default' : 'transparent',
        '&:hover': {
          backgroundColor: selected ? 'background.default' : 'action.hover',
        },
        '&:hover .label-actions': {
          opacity: 1,
        },
      }}
    >
      <Checkbox
        data-vy="labels-item-checkbox"
        checked={selected}
        size="small"
        disableRipple
        sx={{
          padding: 0,
          color: 'text.secondary',
          '&.Mui-checked': {
            color: 'primary.main',
          },
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      />
      <ColorDot color={label.color} sizeOverride="8px" />
      <Typography
        variant="body2"
        sx={{ fontSize: 14, fontWeight: 300, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label.name}
      </Typography>
      {(onEdit || onDelete) && (
        <Box
          className="label-actions"
          sx={{
            display: 'flex',
            gap: 0.5,
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          {onEdit && (
            <IconButton data-vy="labels-item-edit" size="small" onClick={handleEditClick} sx={{ padding: '4px' }}>
              <EditIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton data-vy="labels-item-delete" size="small" onClick={handleDeleteClick} sx={{ padding: '4px' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};
